import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'

import type { Expense } from '@/lib/api/expense-api'
import type { ExpenseFormValues } from '@/components/expenses/expense-form'
import type { LearnPromptState } from '@/components/expenses/expense-learn-prompt-banner'
import type { FilterSearchParams } from '@/components/common/filters/types'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { PageBackButton } from '@/components/common/PageBackButton'
import { ContextualHelpLink } from '@/components/common/contextual-help-link'
import { Badge } from '@/components/ui/badge'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { Button } from '@/components/ui/button'
import { ListCard } from '@/components/common/ListCard'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { STICKY_ACTIONS_CELL } from '@/components/ui/table-sticky'
import { ExpenseBulkCategoryDialog } from '@/components/expenses/expense-bulk-category-dialog'
import { ExpenseDuplicateActions } from '@/components/expenses/expense-duplicate-actions'
import { ExpenseForm } from '@/components/expenses/expense-form'
import { ExpenseLearnPromptBanner } from '@/components/expenses/expense-learn-prompt-banner'
import { ExpenseSyncUploadButton } from '@/components/expenses/expense-sync-upload-button'
import {
    pickExpenseId,
    useExpenseDeepLink,
} from '@/components/expenses/use-expense-deep-link'
import { useAuth } from '@/hooks/use-auth'
import { useExpenseCategories } from '@/hooks/use-expense-category-query'
import {
    useCreateExpense,
    useDeleteExpense,
    useExpenses,
    useUpdateExpense,
} from '@/hooks/use-expense-query'
import { formatExpenseDateTimeVN, formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { cn, twJoin } from '@/lib/utils'
import { EXPENSES_FILTERS } from '@/lib/schemas/expense-search'

const PER_PAGE = 50

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/accounting/expenses',
)({
    component: ExpensesPage,
    // Trang + bộ lọc đẩy lên search param → reload / share link / back giữ
    // nguyên trạng thái đang xem. `expense` (M-143): mở thẳng popup chi tiết
    // một khoản chi (mã phiếu chi ở Báo cáo thu chi link tới đây).
    validateSearch: (raw: Record<string, unknown>): FilterSearchParams => {
        const search = EXPENSES_FILTERS.validateSearch(raw)
        const expense = pickExpenseId(raw.expense)
        return expense ? { ...search, expense } : search
    },
})

function ExpensesPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('expenses.write')
    const canDelete = hasPermission('expenses.delete')
    const canSync = hasPermission('expenses.sync')

    const navigate = useNavigate({ from: '/accounting/expenses' })
    const urlSearch = Route.useSearch()

    // Chip lọc theo hạng mục cần TÊN, URL chỉ có id → tra runtime rồi truyền.
    const { data: catRes } = useExpenseCategories({
        is_active: true,
        per_page: 100,
    })
    const categories = catRes?.data ?? []
    const categoryName = categories.find(
        (c) => c.id === (urlSearch.category_id as string | undefined),
    )?.name

    // `filters` chứ không phải `values`: hàm submit của form bên dưới đã dùng
    // tên `values` cho dữ liệu form.
    const {
        values: filters,
        barProps,
        page,
        setPage,
    } = useListFilters({
        def: EXPENSES_FILTERS,
        search: urlSearch,
        onSearchChange: (next, opts) =>
            void navigate({
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        columns: 4,
        chipLabels: {
            category_id: categoryName
                ? `${m.expense_category_filter_label()}: ${categoryName}`
                : undefined,
        },
    })

    const [editing, setEditing] = useState<Expense | null>(null)
    const [open, setOpen] = useState(false)
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
    const [bulkOpen, setBulkOpen] = useState(false)
    const [learnPrompt, setLearnPrompt] = useState<LearnPromptState | null>(
        null,
    )
    const workflow = useWorkflowTracking(
        editing ? 'expense_update' : 'expense_create',
        {
            active: open,
            attemptKey: editing?.id ?? 'new',
            operation: editing ? 'update' : 'create',
        },
    )
    // Tách popup khoản chi từ sao kê (có nội dung chuyển khoản, M-75) khỏi
    // khoản nhập tay trên cùng workflow expense_update.
    const editingSource = editing
        ? editing.balance_event_id
            ? 'bank_statement'
            : 'manual'
        : undefined
    useEffect(() => {
        if (open && editingSource) {
            workflow.setContext({ expenseSource: editingSource })
        }
    }, [open, editingSource, workflow])

    useExpenseDeepLink({
        expenseId:
            typeof urlSearch.expense === 'string'
                ? urlSearch.expense
                : undefined,
        enabled: canWrite,
        open,
        onOpen: (expense) => {
            setEditing(expense)
            setOpen(true)
        },
        onClear: () =>
            void navigate({
                search: (prev) => ({ ...prev, expense: undefined }),
                replace: true,
            }),
    })

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu. Ba
    // bộ lọc nhanh HD-160 là câu hỏi có/không độc lập, kết hợp được với nhau.
    const { data: listRes } = useExpenses({
        search: filters.q || undefined,
        // remoteSelectFilter's default value is the literal string "all"
        // (see remote-select-filter.tsx), not "" — `|| undefined` alone never
        // caught it, so every visit without an explicit category filter sent
        // category_id=all and the backend 400'd on the malformed UUID.
        category_id:
            filters.category_id && filters.category_id !== 'all'
                ? filters.category_id
                : undefined,
        payment_method:
            filters.payment_method === 'all'
                ? undefined
                : filters.payment_method,
        payment_status:
            filters.payment_status === 'all'
                ? undefined
                : filters.payment_status,
        category_source:
            filters.uncategorized === 'yes' ? 'fallback' : undefined,
        possible_duplicate:
            filters.possible_duplicate === 'yes' ? true : undefined,
        bank_sourced: filters.bank_sourced === 'yes' ? true : undefined,
        from_date: filters.from_date || undefined,
        to_date: filters.to_date || undefined,
        page,
        per_page: PER_PAGE,
    })
    const rows = listRes?.data ?? []
    const meta = listRes?.meta
    const lastPage = Math.max(1, meta?.total_pages ?? 1)

    // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá hẹp — trường hợp thứ
    // hai phải cho người dùng lối thoát ngay tại chỗ (R10).
    const filtering = barProps.activeCount > 0 || !!filters.q
    const emptyState = (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="text-muted-foreground">
                {filtering ? m.list_empty_filtered() : m.expense_list_empty()}
            </span>
            {filtering ? (
                <ClearFilterButton active onClear={barProps.onClear} />
            ) : null}
        </div>
    )

    // Xoá dòng cuối của trang cuối làm trang hiện tại vượt quá tổng số trang —
    // kéo về trang cuối còn dữ liệu thay vì hiển thị bảng rỗng. Dùng replace để
    // nút back không rơi lại đúng trang vừa biến mất.
    useEffect(() => {
        if (!meta || page <= lastPage) return
        setPage(lastPage)
    }, [meta, page, lastPage, setPage])

    const createMut = useCreateExpense()
    const updateMut = useUpdateExpense(editing?.id ?? '')

    const allSelected =
        rows.length > 0 && rows.every((r) => selectedIds.has(r.id))
    function toggleSelectAll() {
        setSelectedIds(allSelected ? new Set() : new Set(rows.map((r) => r.id)))
    }
    function toggleSelect(id: string) {
        setSelectedIds((prev) => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    return (
        <div className="space-y-4 page-pad">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                    <PageBackButton className="-my-2 md:-my-1" />
                    <h1 className="text-xl font-semibold">
                        {m.expense_title()}
                    </h1>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                    <ContextualHelpLink />
                    {canSync && <ExpenseSyncUploadButton />}
                    {canWrite && (
                        <Button
                            size="sm"
                            onClick={() => {
                                setEditing(null)
                                setOpen(true)
                            }}
                        >
                            <Plus className="size-4" /> {m.expense_new()}
                        </Button>
                    )}
                </div>
            </div>

            <ListFilterBar {...barProps} />

            {canWrite && selectedIds.size > 0 && (
                <div className="flex items-center gap-2 rounded-md border bg-muted/40 p-2 text-sm">
                    <span>
                        {m.expense_bulk_selected_count({
                            count: String(selectedIds.size),
                        })}
                    </span>
                    <Button size="sm" onClick={() => setBulkOpen(true)}>
                        {m.expense_bulk_change_category()}
                    </Button>
                </div>
            )}

            {learnPrompt && (
                <ExpenseLearnPromptBanner
                    prompt={learnPrompt}
                    onDismiss={() => setLearnPrompt(null)}
                />
            )}

            {/* Mobile: card list */}
            <div className="space-y-2 md:hidden">
                {rows.length === 0
                    ? emptyState
                    : rows.map((row) => (
                          <ListCard
                              key={row.id}
                              title={
                                  <span className="font-medium">
                                      {row.vendor_name}
                                  </span>
                              }
                              subtitle={row.category?.name ?? '—'}
                              badge={
                                  <div className="flex flex-wrap items-center justify-end gap-1">
                                      <Badge
                                          variant={
                                              row.payment_status === 'paid'
                                                  ? 'default'
                                                  : 'secondary'
                                          }
                                      >
                                          {row.payment_status === 'paid'
                                              ? m.expense_payment_status_paid()
                                              : m.expense_payment_status_unpaid()}
                                      </Badge>
                                      {row.possible_duplicate && (
                                          <Badge variant="destructive">
                                              {m.expense_possible_duplicate_badge()}
                                          </Badge>
                                      )}
                                  </div>
                              }
                              actions={
                                  <>
                                      {row.possible_duplicate &&
                                          row.balance_event_id && (
                                              <ExpenseDuplicateActions
                                                  expenseId={row.id}
                                                  showBadge={false}
                                              />
                                          )}
                                      {canWrite ? (
                                          <Button
                                              variant="ghost"
                                              size="icon"
                                              aria-label={m.common_edit()}
                                              onClick={() => {
                                                  setEditing(row)
                                                  setOpen(true)
                                              }}
                                          >
                                              <Pencil className="size-4" />
                                          </Button>
                                      ) : null}
                                      {canDelete ? (
                                          <ExpenseDeleteButton
                                              expenseId={row.id}
                                          />
                                      ) : null}
                                  </>
                              }
                              metric={{
                                  label: m.expense_amount(),
                                  value: formatVnd(row.amount),
                              }}
                              fields={[
                                  {
                                      label: m.expense_date(),
                                      value: formatExpenseDateTimeVN(
                                          row.expense_date,
                                          row.spent_at,
                                      ),
                                  },
                                  {
                                      label: m.expense_payment_method(),
                                      value:
                                          row.payment_method === 'cash'
                                              ? m.expense_payment_method_cash()
                                              : m.expense_payment_method_bank_transfer(),
                                      full: true,
                                  },
                                  ...(row.bank_transfer_content
                                      ? [
                                            {
                                                label: m.expense_sync_content(),
                                                value: row.bank_transfer_content,
                                                full: true,
                                            },
                                        ]
                                      : []),
                                  ...(row.note
                                      ? [
                                            {
                                                label: m.expense_note(),
                                                value: row.note,
                                                full: true,
                                            },
                                        ]
                                      : []),
                              ]}
                          />
                      ))}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            {canWrite && (
                                <TableHead className="w-8">
                                    <input
                                        type="checkbox"
                                        className="size-4 align-middle"
                                        aria-label={m.expense_bulk_select_all()}
                                        checked={allSelected}
                                        onChange={toggleSelectAll}
                                    />
                                </TableHead>
                            )}
                            <TableHead>{m.expense_date()}</TableHead>
                            <TableHead>{m.expense_vendor_name()}</TableHead>
                            <TableHead>{m.expense_sync_content()}</TableHead>
                            <TableHead>{m.expense_category_title()}</TableHead>
                            <TableHead className="text-right">
                                {m.expense_amount()}
                            </TableHead>
                            <TableHead>{m.expense_payment_method()}</TableHead>
                            <TableHead>{m.expense_payment_status()}</TableHead>
                            <TableHead>{m.expense_note()}</TableHead>
                            <TableHead className="min-w-56">
                                {m.expense_possible_duplicate_badge()}
                            </TableHead>
                            <TableHead className={STICKY_ACTIONS_CELL} />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={11}>{emptyState}</TableCell>
                            </TableRow>
                        )}
                        {rows.map((row) => (
                            <TableRow key={row.id} className="group">
                                {canWrite && (
                                    <TableCell>
                                        <input
                                            type="checkbox"
                                            className="size-4 align-middle"
                                            aria-label={row.vendor_name}
                                            checked={selectedIds.has(row.id)}
                                            onChange={() =>
                                                toggleSelect(row.id)
                                            }
                                        />
                                    </TableCell>
                                )}
                                <TableCell>
                                    {formatExpenseDateTimeVN(
                                        row.expense_date,
                                        row.spent_at,
                                    )}
                                </TableCell>
                                <ExpenseLongTextCell
                                    text={row.vendor_name}
                                    className="min-w-40 max-w-56"
                                />
                                <ExpenseLongTextCell
                                    text={row.bank_transfer_content}
                                    lines={3}
                                    className="min-w-72 max-w-[28rem]"
                                />
                                <TableCell>
                                    {row.category?.name ?? '—'}
                                </TableCell>
                                <TableCell className="text-right font-mono">
                                    {formatVnd(row.amount)}
                                </TableCell>
                                <TableCell>
                                    {row.payment_method === 'cash'
                                        ? m.expense_payment_method_cash()
                                        : m.expense_payment_method_bank_transfer()}
                                </TableCell>
                                <TableCell>
                                    <Badge
                                        variant={
                                            row.payment_status === 'paid'
                                                ? 'default'
                                                : 'secondary'
                                        }
                                    >
                                        {row.payment_status === 'paid'
                                            ? m.expense_payment_status_paid()
                                            : m.expense_payment_status_unpaid()}
                                    </Badge>
                                </TableCell>
                                <ExpenseLongTextCell text={row.note} />
                                <TableCell className="align-top">
                                    {row.possible_duplicate &&
                                        row.balance_event_id && (
                                            <ExpenseDuplicateActions
                                                expenseId={row.id}
                                            />
                                        )}
                                </TableCell>
                                <TableCell
                                    className={cn(
                                        'text-right space-x-1',
                                        STICKY_ACTIONS_CELL,
                                    )}
                                >
                                    {canWrite && (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            aria-label={m.common_edit()}
                                            onClick={() => {
                                                setEditing(row)
                                                setOpen(true)
                                            }}
                                        >
                                            <Pencil className="size-4" />
                                        </Button>
                                    )}
                                    {canDelete && (
                                        <ExpenseDeleteButton
                                            expenseId={row.id}
                                        />
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {meta && meta.total > 0 && (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>
                        {m.expense_total_count({ count: String(meta.total) })}
                    </span>
                    {meta.total_pages > 1 && (
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={page === 1}
                                onClick={() => setPage(page - 1)}
                            >
                                {m.common_pagination_prev()}
                            </Button>
                            <span className="flex items-center px-2">
                                {page} / {meta.total_pages}
                            </span>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={page >= meta.total_pages}
                                onClick={() => setPage(page + 1)}
                            >
                                {m.common_pagination_next()}
                            </Button>
                        </div>
                    )}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {editing ? m.expense_edit() : m.expense_new()}
                        </DialogTitle>
                    </DialogHeader>
                    <ExpenseForm
                        initial={editing}
                        categories={categories}
                        onValidationError={() =>
                            workflow.step('validation_failed')
                        }
                        onInteract={() => workflow.interact('details')}
                        onSubmit={async (values: ExpenseFormValues) => {
                            const wasBankSourced = Boolean(
                                editing?.balance_event_id,
                            )
                            const categoryChanged =
                                editing != null &&
                                editing.category_id !== values.category_id
                            if (editing) {
                                await workflow.run(
                                    () => updateMut.mutateAsync(values),
                                    (res) => res.data?.id,
                                )
                            } else {
                                await workflow.run(
                                    () => createMut.mutateAsync(values),
                                    (res) => res.data?.id,
                                )
                            }
                            // Học từ sửa tay: chỉ gợi ý khi vừa đổi danh mục
                            // của một khoản chi TỪ SAO KÊ NGÂN HÀNG.
                            if (wasBankSourced && categoryChanged && editing) {
                                const category = categories.find(
                                    (c) => c.id === values.category_id,
                                )
                                setLearnPrompt({
                                    counterpartyName:
                                        editing.bank_counterparty_name ??
                                        editing.vendor_name,
                                    counterpartyAccount:
                                        editing.bank_counterparty_account,
                                    categoryId: values.category_id,
                                    categoryName: category?.name ?? '',
                                })
                            }
                            setOpen(false)
                        }}
                        submitting={createMut.isPending || updateMut.isPending}
                    />
                </DialogContent>
            </Dialog>

            <ExpenseBulkCategoryDialog
                ids={[...selectedIds]}
                categories={categories}
                open={bulkOpen}
                onOpenChange={setBulkOpen}
                onDone={() => {
                    setBulkOpen(false)
                    setSelectedIds(new Set())
                }}
            />
        </div>
    )
}

// Ô văn bản dài (người nhận, nội dung chuyển khoản, ghi chú): giới hạn số
// dòng để bảng không phình, rê chuột xem bản đầy đủ qua title. Class
// line-clamp phải là chuỗi tĩnh để Tailwind sinh ra được.
const LINE_CLAMP = { 2: 'line-clamp-2', 3: 'line-clamp-3' } as const

function ExpenseLongTextCell({
    text,
    lines = 2,
    className = 'min-w-48 max-w-[20rem]',
}: {
    text?: string
    lines?: keyof typeof LINE_CLAMP
    className?: string
}) {
    if (!text) {
        return <TableCell className="text-muted-foreground">—</TableCell>
    }
    return (
        <TableCell className={cn('whitespace-normal', className)}>
            <p
                className={twJoin(
                    LINE_CLAMP[lines],
                    'text-sm [overflow-wrap:anywhere]',
                )}
                title={text}
            >
                {text}
            </p>
        </TableCell>
    )
}

function ExpenseDeleteButton({ expenseId }: { expenseId: string }) {
    const deletion = useDeleteExpense()
    const workflow = useWorkflowTracking('expense_delete', {
        autoStart: false,
        attemptKey: expenseId,
        operation: 'delete',
    })
    return (
        <Button
            variant="ghost"
            size="icon"
            aria-label={m.common_delete()}
            onClick={() => {
                workflow.step('warning_shown')
                if (!window.confirm(m.expense_delete_confirm())) {
                    workflow.finish('closed')
                    return
                }
                workflow.step('warning_confirmed')
                void workflow
                    .run(() => deletion.mutateAsync(expenseId))
                    .catch(() => {})
            }}
        >
            <Trash2 className="size-4" />
        </Button>
    )
}
